import CustomPartnerService from '../../../../shared/CustomPartner.service';
import { getPartnerName } from '../../main/components/PartnerManager/utils';
import type { AdPartner, AdPartnerBlueprint, AdPartnerWithOnlyDataForSKAN } from '../../main/types/adPartnerTypes';

export class AdPartnerService {
  public static sortByCompanyName<T extends (AdPartner | AdPartnerWithOnlyDataForSKAN)[]>(partners: T): T {
    partners.sort((a, b) => getPartnerName(a).toLowerCase().localeCompare(getPartnerName(b).toLowerCase()));
    return partners;
  }

  public static mergeEntityWithBlueprint(blueprint: AdPartnerBlueprint, entity: AdPartner | null | undefined): AdPartner {
    let mergedConfig: AdPartner;

    /**
     * ad_partner_blueprint entries have a different postback type
     * (Map<String, Postback>) than ad_partner entries (Array<Postback>),
     * so make sure to return the AdPartner type.
     */
    const sanitizedBlueprint = { ...blueprint, postbacks: [] };
    mergedConfig = { ...sanitizedBlueprint } as unknown as AdPartner;

    if (entity) {
      const sanitizedEntity = { ...entity, postbacks: entity.postbacks || [] };
      mergedConfig = { ...mergedConfig, ...sanitizedEntity };
    } else {
      /**
       * Use plain blueprint (without ad network's entity) only if
       * entity's config doesn't exist.
       */
      mergedConfig.neverConfigured = true;
    }

    /**
     * Names of all events for all pre-defined postbacks are needed
     * because we use them to determine whether a URL is needed as input
     * while creating a postback.
     * If an event for which a postback is being created has a pre-defined
     * postback on the blueprint, then we wouldn't ask for a URL from the
     * user and use the pre-defined postback URL/template
     * If the event doesn't have a pre-defined postback on the blueprint, then
     * we try to fallback to custom_event on the blueprint. If custom_event
     * doesn't exist on the blueprint, then we need URL from the user
     */
    mergedConfig.eventsInBlueprintPostbacks = Object.keys(blueprint.postbacks || {});
    return mergedConfig;
  }

  public static mergeAppsWithBlueprints(
    appConfigs: Record<string, AdPartner>,
    blueprints: AdPartnerBlueprint[]
  ): AdPartner[] {
    const mergedConfigs: AdPartner[] = blueprints.map((blueprint: AdPartnerBlueprint): AdPartner => {
      // find appConfig based on machine_name
      const appConfig = appConfigs ? appConfigs[blueprint.machine_name] : null;
      return this.mergeEntityWithBlueprint(blueprint, appConfig);
    });
    // There may be ad networks for which there are no blueprints
    const nonBlueprintedConfigs: AdPartner[] = Object.keys(appConfigs).reduce((acc, machineName) => {
      const foundBlueprint = blueprints.find((blueprint) => blueprint.machine_name === machineName);

      if (
        !foundBlueprint &&
        CustomPartnerService.isCustomPartner({ machine_name: machineName }) &&
        appConfigs &&
        appConfigs[machineName]
      ) {
        return [...acc, appConfigs[machineName]];
      }

      return acc;
    }, [] as AdPartner[]);
    return mergedConfigs.concat(nonBlueprintedConfigs);
  }
}
